import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Droplets, Eye, EyeOff, Lock, User, Phone, MapPin, BadgeCheck, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FuncionarioAuthPage = () => {
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [stations, setStations] = useState([]);
  const [createdId, setCreatedId] = useState(null);
  
  const [loginData, setLoginData] = useState({ funcionario_id: '', password: '' });
  const [registerData, setRegisterData] = useState({ 
    name: '', phone: '', station_id: '', password: '' 
  });

  useEffect(() => {
    const loadStations = async () => {
      try {
        const res = await axios.get(`${API}/stations`);
        setStations(res.data);
      } catch (err) {
        console.error('Error loading stations:', err);
      }
    };
    loadStations();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/funcionario/login`, loginData);
      // Store token
      localStorage.setItem('halubri_token', res.data.access_token);
      toast.success('Bem-vindo de volta!');
      // Use navigate with state refresh to ensure AuthContext picks up the token
      window.location.href = '/admin';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'ID ou palavra-passe inválidos');
    }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerData.station_id) {
      toast.error('Seleccione uma estação');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/funcionario/register`, registerData);
      setCreatedId(res.data.user.funcionario_id);
      toast.success('Conta de funcionário criada!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar conta');
    }
    setLoading(false);
  };

  const goToAdminWithNewAccount = () => {
    // Auto-login with new credentials
    setLoginData({ funcionario_id: createdId, password: registerData.password });
    handleLoginWithCredentials(createdId, registerData.password);
  };

  const handleLoginWithCredentials = async (id, pass) => {
    try {
      const res = await axios.post(`${API}/auth/funcionario/login`, {
        funcionario_id: id,
        password: pass
      });
      localStorage.setItem('halubri_token', res.data.access_token);
      window.location.href = '/admin';
    } catch (err) {
      toast.error('Erro ao entrar automaticamente');
    }
  };

  // Show success screen after registration
  if (createdId) {
    return (
      <div className="min-h-screen bg-zinc-900 flex flex-col items-center justify-center px-4">
        <div className="bg-zinc-800 rounded-2xl p-8 max-w-md w-full text-center border border-zinc-700">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <BadgeCheck className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="font-barlow text-2xl font-bold text-white mb-2">
            Conta Criada com Sucesso!
          </h2>
          <p className="text-zinc-400 mb-6">
            Guarde o seu ID de funcionário. Vai precisar dele para entrar.
          </p>
          
          <div className="bg-zinc-900 rounded-xl p-4 mb-6">
            <p className="text-zinc-500 text-sm mb-1">O seu ID de Funcionário</p>
            <p className="font-barlow text-3xl font-bold text-sonangol-orange tracking-wider">
              {createdId}
            </p>
          </div>
          
          <Button 
            className="w-full h-12 bg-sonangol-red hover:bg-red-700 font-barlow font-bold uppercase"
            onClick={goToAdminWithNewAccount}
            data-testid="go-to-admin-btn"
          >
            Entrar no Painel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-900 flex flex-col">
      {/* Header */}
      <div className="bg-zinc-800 pt-6 pb-8 px-4 border-b border-zinc-700">
        <div className="flex items-center gap-4 mb-4">
          <Link to="/auth" className="tap-target text-zinc-400 hover:text-white" data-testid="auth-back-link">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <span className="text-zinc-500 text-sm">Voltar para Cliente</span>
        </div>
        <div className="text-center">
          <div className="w-16 h-16 mx-auto bg-sonangol-orange rounded-xl flex items-center justify-center shadow-lg mb-4">
            <Droplets className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-barlow text-2xl font-bold text-white uppercase tracking-tight">
            Portal Funcionário
          </h1>
          <p className="text-zinc-400 text-sm mt-1 font-manrope">SONANGOL · Gestão de Pedidos</p>
        </div>
      </div>

      {/* Auth form */}
      <div className="flex-1 px-4 py-6">
        <div className="bg-zinc-800 rounded-2xl p-6 max-w-md mx-auto border border-zinc-700">
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-zinc-700">
              <TabsTrigger value="login" className="data-[state=active]:bg-sonangol-orange" data-testid="func-login-tab">
                Entrar
              </TabsTrigger>
              <TabsTrigger value="register" className="data-[state=active]:bg-sonangol-orange" data-testid="func-register-tab">
                Registar
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="func-id" className="text-zinc-300">ID de Funcionário</Label>
                  <div className="relative">
                    <BadgeCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <Input
                      id="func-id"
                      type="text"
                      placeholder="FUNC-MUT-0001"
                      className="pl-10 h-12 bg-zinc-900 border-zinc-600 text-white placeholder:text-zinc-500"
                      value={loginData.funcionario_id}
                      onChange={(e) => setLoginData({ ...loginData, funcionario_id: e.target.value.toUpperCase() })}
                      required
                      data-testid="func-login-id-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="func-login-password" className="text-zinc-300">Palavra-passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <Input
                      id="func-login-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      className="pl-10 pr-10 h-12 bg-zinc-900 border-zinc-600 text-white"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      data-testid="func-login-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 bg-sonangol-orange hover:bg-orange-600 font-barlow font-bold uppercase tracking-wider"
                  disabled={loading}
                  data-testid="func-login-submit-btn"
                >
                  {loading ? 'A entrar...' : 'Entrar'}
                </Button>
                
                {/* Super Admin Link - Hidden */}
                <div className="text-center pt-4 border-t border-zinc-700 mt-4">
                  <Link 
                    to="/admin/login" 
                    className="text-zinc-500 hover:text-zinc-300 text-xs transition-colors"
                  >
                    Acesso Administrativo
                  </Link>
                </div>
              </form>
            </TabsContent>
            
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="func-name" className="text-zinc-300">Nome Completo</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <Input
                      id="func-name"
                      type="text"
                      placeholder="João Silva"
                      className="pl-10 h-12 bg-zinc-900 border-zinc-600 text-white placeholder:text-zinc-500"
                      value={registerData.name}
                      onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                      required
                      data-testid="func-register-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="func-phone" className="text-zinc-300">Telefone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <Input
                      id="func-phone"
                      type="tel"
                      placeholder="+244 923 456 789"
                      className="pl-10 h-12 bg-zinc-900 border-zinc-600 text-white placeholder:text-zinc-500"
                      value={registerData.phone}
                      onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                      required
                      data-testid="func-register-phone-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-300">Estação de Serviço</Label>
                  <Select 
                    value={registerData.station_id} 
                    onValueChange={(value) => {
                      console.log('Station selected:', value);
                      setRegisterData(prev => ({ ...prev, station_id: value }));
                    }}
                    required
                  >
                    <SelectTrigger 
                      className="h-12 bg-zinc-900 border-zinc-600 text-white" 
                      data-testid="func-station-select"
                    >
                      <MapPin className="w-5 h-5 text-zinc-500 mr-2" />
                      <SelectValue placeholder="Seleccione a sua estação" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {stations.length === 0 ? (
                        <SelectItem value="loading" disabled>Carregando estações...</SelectItem>
                      ) : (
                        stations.map((station) => (
                          <SelectItem 
                            key={station.id} 
                            value={station.id}
                            data-testid={`station-option-${station.id}`}
                            className="text-white hover:bg-zinc-700 focus:bg-zinc-700"
                          >
                            {station.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {registerData.station_id && (
                    <p className="text-xs text-green-500">
                      Estação selecionada: {stations.find(s => s.id === registerData.station_id)?.name}
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="func-register-password" className="text-zinc-300">Criar Palavra-passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <Input
                      id="func-register-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      className="pl-10 pr-10 h-12 bg-zinc-900 border-zinc-600 text-white"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      required
                      data-testid="func-register-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="bg-zinc-900/50 rounded-xl p-3 border border-zinc-700">
                  <p className="text-zinc-400 text-xs">
                    <span className="text-sonangol-orange font-semibold">Nota:</span> Após o registo, receberá um ID único (ex: FUNC-MUT-0001) que usará para entrar no sistema.
                  </p>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 bg-sonangol-orange hover:bg-orange-600 font-barlow font-bold uppercase tracking-wider"
                  disabled={loading}
                  data-testid="func-register-submit-btn"
                >
                  {loading ? 'A criar...' : 'Criar Conta de Funcionário'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Footer */}
      <div className="py-6 text-center text-zinc-600 text-xs font-manrope">
        © 2025 Sonangol E.P. · Portal de Funcionários
      </div>
    </div>
  );
};

export default FuncionarioAuthPage;
